import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../../../components/button/button";
import dataVideo from "../../../assets/videos/course_intro.mp4"; // Replace with your video

// Accessible accordion component
const Accordion = ({ children }) => (
  <div className="border border-gray-200 divide-y rounded-2xl overflow-hidden shadow-sm">
    {children}
  </div>
);

const AccordionItem = ({ title, content }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="p-4 bg-white hover:bg-gray-50 transition">
      <h3>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full justify-between items-center text-left text-lg font-semibold text-[#0f172a] focus:outline-none transition"
          aria-expanded={isOpen}
        >
          <span>{title}</span>
          <motion.span
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-gray-600 text-xl"
          >
            ▶
          </motion.span>
        </button>
      </h3>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden mt-3 text-gray-700"
          >
            <ul className="list-disc pl-6 space-y-2">
              {content.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const CourseDescription = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const accordionData = [
    {
      title: "📈 Introduction to Data Analysis",
      content: [
        "Understanding the role of a data analyst in modern organizations",
        "The data analysis workflow: from raw data to insights",
        "Key tools and software every data analyst must know",
        "Real-world case studies of data-driven decision making",
      ],
    },
    {
      title: "💻 Excel & Google Sheets Mastery",
      content: [
        "Data cleaning, sorting, and manipulation using Excel",
        "Creating pivot tables, dashboards, and advanced formulas",
        "Visualization using Excel charts and conditional formatting",
        "Real-life business analysis using spreadsheets",
      ],
    },
    {
      title: "🧠 Python for Data Analysis",
      content: [
        "Python basics for beginners (variables, loops, and functions)",
        "Data manipulation using Pandas and NumPy",
        "Data visualization with Matplotlib and Seaborn",
        "Exploratory Data Analysis (EDA) and storytelling with data",
      ],
    },
    {
      title: "🗃 SQL & Databases",
      content: [
        "Introduction to relational databases and SQL syntax",
        "Querying, filtering, and aggregating data efficiently",
        "Joining multiple tables and creating analytical reports",
        "Real-world SQL use cases in business and analytics",
      ],
    },
    {
      title: "📊 Power BI & Data Visualization",
      content: [
        "Creating interactive dashboards and data stories",
        "Connecting Power BI to multiple data sources",
        "Building visual insights that drive decision-making",
        "Sharing dashboards and automating reports",
      ],
    },
    {
      title: "🚀 Data-Driven Decision Making",
      content: [
        "Understanding KPIs and business metrics",
        "Turning analysis into strategy and storytelling",
        "Presenting findings with confidence and clarity",
        "Final capstone project: analyze and present real data",
      ],
    },
  ];

  return (
    <div className="w-full bg-gradient-to-b from-white to-[#f9fafb] text-[#1a1a1a] font-inter py-10 px-6 sm:px-10 lg:px-20 relative overflow-hidden">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-left mb-10"
      >
        <h1 className="text-4xl font-extrabold tracking-tight text-[#0f172a]">
          Data Analysis Bootcamp
        </h1>
        <p className="text-lg text-gray-600 mt-2 max-w-3xl">
          Unlock the power of data! Learn to collect, clean, visualize, and
          interpret data to drive impactful business decisions across industries.
        </p>
      </motion.div>

      {/* VIDEO */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-4xl mx-auto mb-12 rounded-2xl overflow-hidden shadow-xl"
      >
        <video src={dataVideo} controls className="w-full h-[23rem] object-cover" />
      </motion.div>

      {/* DESCRIPTION */}
      <section className="max-w-5xl mx-auto mb-12">
        <h2 className="text-2xl font-bold mb-4 text-[#0f172a]">
          Why Become a Data Analyst?
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          Data Analysts are the storytellers of the digital age. They translate
          numbers into insights that influence billion-naira decisions, shape
          products, and drive innovation. In this bootcamp, you’ll gain the
          analytical and technical expertise to turn raw data into business gold.
        </p>
      </section>

      {/* WHAT YOU’LL LEARN */}
      <section className="max-w-5xl mx-auto mb-16">
        <h2 className="text-2xl font-bold mb-6 text-[#0f172a]">
          What You’ll Learn
        </h2>
        <Accordion>
          {accordionData.map((item, index) => (
            <AccordionItem key={index} title={item.title} content={item.content} />
          ))}
        </Accordion>
      </section>

      {/* COURSE COST */}
      <section className="max-w-6xl mx-auto bg-white rounded-3xl shadow-lg border border-gray-100 p-10 mb-16">
        <h2 className="text-3xl font-extrabold mb-10 text-center text-[#0f172a]">
          💼 Course Cost Breakdown
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              title: "Data Analysis (Weekdays)",
              price: "₦350,000",
              color: "from-pink-500 to-purple-500",
              modules: ["Monday 10AM - 3PM", "Wednesday 10AM - 1PM"],
            },
            {
              title: "Data Analysis (Weekends)",
              price: "₦350,000",
              color: "from-indigo-500 to-blue-500",
              modules: ["Saturday 10AM - 4PM", "Sunday 12PM - 4PM"],
            },
          ].map((course, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6"
            >
              <h3
                className={`text-xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r ${course.color}`}
              >
                {course.title}
              </h3>
              <p className="text-2xl font-extrabold text-[#0f172a] mb-6">
                {course.price}
              </p>
              <ul className="space-y-3 text-gray-700 text-sm">
                {course.modules.map((mod, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 hover:bg-gray-100 transition"
                  >
                    <span className="text-[#0f172a] font-medium">•</span>
                    <span>{mod}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* AUTO POPUP */}
      {showPopup && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-8 right-8 bg-[#0f172a] text-white rounded-xl shadow-2xl p-6 w-80 z-50"
        >
          <h3 className="text-lg font-bold mb-2">🎉 Get 15% Off!</h3>
          <p className="text-sm mb-4 text-gray-200">
            Enroll within 24 hours and enjoy a 15% discount on your tuition.
          </p>
          <Button
            btnText="Claim Discount"
            btnClassName="!bg-white !text-[#0f172a] font-semibold w-full py-2 rounded-lg hover:!bg-gray-100 transition"
            onClick={() => setShowDiscountModal(true)}
          />
        </motion.div>
      )}

        {/* FLOATING DISCOUNT BUTTON */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        className="fixed bottom-8 left-8 bg-[#0f172a] text-white font-semibold px-5 py-3 rounded-full shadow-xl hover:bg-[#1e293b] transition z-50"
        onClick={() => setShowDiscountModal(true)}
      >
        💰 Get 20% Discount
      </motion.button>

      {/* DISCOUNT MODAL */}
      <AnimatePresence>
        {showDiscountModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black backdrop-blur-sm z-40"
              onClick={() => setShowDiscountModal(false)}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="fixed z-50 inset-0 flex items-center justify-center"
            >
              <div className="relative bg-white w-96 rounded-2xl shadow-2xl p-6 text-center">
                {/* Close Button */}
          <button
            onClick={() => setShowDiscountModal(false)}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-semibold transition"
            aria-label="Close discount modal"
          >
            &times;
          </button>

                <h3 className="text-2xl font-bold text-[#0f172a] mb-2">
                  🎁 Claim Your 15% Discount
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Fill in your details below to secure your discount.
                </p>
                <form className="space-y-3">
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                    <option value="">Select Course</option>
                    <option value="data_engineering">Data Engineering</option>
                    <option value="data_analysis">Data Analysis </option>
                  </select>
                  <Button
                    btnText="Pay Now & Get 15% Off"
                    btnClassName="!bg-[#0f172a] text-white w-full py-2 rounded-lg hover:!bg-[#1e293b] transition"
                    onClick={() => {
                      window.open("https://paystack.com/buy/data-analysis--data-engineering-ezsole", "_blank");
                    }}
                  />
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CourseDescription;
